// 근무자 선정 모달
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  Spinner,
  Checkbox
} from '@chakra-ui/react';
import { SearchIcon, ChevronRightIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from 'config/firebase';
import {
  addSemesterWorkersBatch,
  removeSemesterWorkersBatch,
  getSemesterWorkers
} from 'utils/firestoreUtils';

function WorkerSelectionModal({ isOpen, onClose, semesterId, semester, onSaved }) {
  const toast = useToast();

  const [allUsers, setAllUsers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);

  useEffect(() => {
    if (isOpen && semesterId) {
      loadData();
    }
  }, [isOpen, semesterId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 모든 candidate 사용자 조회
      const usersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'candidate')
      );
      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 이미 선정된 근무자 조회
      const workers = await getSemesterWorkers(semesterId);

      setAllUsers(usersData);
      setSelectedWorkers(workers.map(w => ({
        userId: w.userId,
        name: w.userName,
        email: w.userEmail,
        phone: w.userPhone,
        maxWorkHours: w.maxWorkHours,
        workerId: w.id
      })));
    } catch (error) {
      console.error('데이터 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '데이터를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(user => {
    const isNotSelected = !selectedWorkers.find(w => w.userId === user.id);
    const matchesSearch = searchTerm === '' || 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);
    return isNotSelected && matchesSearch;
  });

  const handleMoveRight = () => {
    const toAdd = leftSelected.map(userId => {
      const user = allUsers.find(u => u.id === userId);
      return {
        userId: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        maxWorkHours: semester?.defaultMaxWorkHours || 40
      };
    });
    setSelectedWorkers([...selectedWorkers, ...toAdd]);
    setLeftSelected([]);
  };

  const handleMoveLeft = () => {
    const remaining = selectedWorkers.filter(w => !rightSelected.includes(w.userId));
    setSelectedWorkers(remaining);
    setRightSelected([]);
  };

  const handleSave = async () => {
    if (selectedWorkers.length === 0) {
      toast({
        title: '선정 필요',
        description: '최소 1명 이상의 근무자를 선정해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    setSaving(true);
    try {
      // 기존 근무자 중 제거된 사람 찾기
      const currentWorkerIds = selectedWorkers.filter(w => w.workerId).map(w => w.workerId);
      const originalWorkerIds = selectedWorkers.filter(w => w.workerId).map(w => w.workerId);
      
      // 새로 추가할 근무자 (workerId가 없는 경우)
      const newWorkers = selectedWorkers.filter(w => !w.workerId);

      // 제거할 근무자 ID
      const initialWorkers = await getSemesterWorkers(semesterId);
      const toRemove = initialWorkers
        .filter(w => !selectedWorkers.find(sw => sw.userId === w.userId))
        .map(w => w.id);

      // 제거
      if (toRemove.length > 0) {
        await removeSemesterWorkersBatch(toRemove);
      }

      // 추가
      if (newWorkers.length > 0) {
        await addSemesterWorkersBatch(semesterId, newWorkers);
      }

      toast({
        title: '저장 완료',
        description: `${newWorkers.length}명 추가, ${toRemove.length}명 제거되었습니다.`,
        status: 'success',
        duration: 3000
      });

      onSaved();
    } catch (error) {
      console.error('저장 오류:', error);
      toast({
        title: '저장 실패',
        description: error.message || '근무자 저장에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="1400px">
        <ModalHeader>근무자 선정</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {loading ? (
            <Flex justify="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          ) : (
            <Flex gap={4} height="600px">
              {/* 왼쪽: 전체 구인풀 */}
              <Box flex="1" borderWidth="1px" borderRadius="md" p={4}>
                <Text fontWeight="bold" mb={3}>전체 구인풀 ({filteredUsers.length}명)</Text>

                <InputGroup mb={3}>
                  <InputLeftElement>
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="이름, 이메일, 전화번호 검색"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>

                <Box overflowY="auto" height="calc(100% - 90px)">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr>
                        <Th width="40px">
                          <Checkbox
                            isChecked={leftSelected.length === filteredUsers.length && filteredUsers.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setLeftSelected(filteredUsers.map(u => u.id));
                              } else {
                                setLeftSelected([]);
                              }
                            }}
                          />
                        </Th>
                        <Th>이름</Th>
                        <Th>연락처</Th>
                        <Th>회원유형</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredUsers.map((user) => (
                        <Tr key={user.id}>
                          <Td>
                            <Checkbox
                              isChecked={leftSelected.includes(user.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setLeftSelected([...leftSelected, user.id]);
                                } else {
                                  setLeftSelected(leftSelected.filter(id => id !== user.id));
                                }
                              }}
                            />
                          </Td>
                          <Td fontWeight="bold">{user.name}</Td>
                          <Td fontSize="sm">{user.phone || user.email}</Td>
                          <Td>
                            <Badge colorScheme="purple">{user.userType || '-'}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {filteredUsers.length === 0 && (
                    <Text textAlign="center" color="gray.500" mt={10}>
                      {searchTerm ? '검색 결과가 없습니다.' : '사용 가능한 구인풀이 없습니다.'}
                    </Text>
                  )}
                </Box>
              </Box>

              {/* 중앙: 이동 버튼 */}
              <Flex direction="column" justify="center" gap={2}>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={handleMoveRight}
                  isDisabled={leftSelected.length === 0}
                  aria-label="오른쪽으로 이동"
                />
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={handleMoveLeft}
                  isDisabled={rightSelected.length === 0}
                  aria-label="왼쪽으로 이동"
                />
              </Flex>

              {/* 오른쪽: 선정된 근무자 */}
              <Box flex="1" borderWidth="1px" borderRadius="md" p={4} bg="blue.50">
                <Text fontWeight="bold" mb={3}>선정된 근무자 ({selectedWorkers.length}명)</Text>

                <Box overflowY="auto" height="calc(100% - 50px)" mt={3}>
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="blue.50" zIndex={1}>
                      <Tr>
                        <Th width="40px">
                          <Checkbox
                            isChecked={rightSelected.length === selectedWorkers.length && selectedWorkers.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRightSelected(selectedWorkers.map(w => w.userId));
                              } else {
                                setRightSelected([]);
                              }
                            }}
                          />
                        </Th>
                        <Th>이름</Th>
                        <Th>연락처</Th>
                        <Th>최대 근로시간</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedWorkers.map((worker) => (
                        <Tr key={worker.userId}>
                          <Td>
                            <Checkbox
                              isChecked={rightSelected.includes(worker.userId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setRightSelected([...rightSelected, worker.userId]);
                                } else {
                                  setRightSelected(rightSelected.filter(id => id !== worker.userId));
                                }
                              }}
                            />
                          </Td>
                          <Td fontWeight="bold">{worker.name}</Td>
                          <Td fontSize="sm">{worker.phone || worker.email}</Td>
                          <Td fontSize="sm">
                            <Badge colorScheme="green">{worker.maxWorkHours}시간</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                  {selectedWorkers.length === 0 && (
                    <Text textAlign="center" color="gray.500" mt={10}>
                      선정된 근무자가 없습니다.
                    </Text>
                  )}
                </Box>
              </Box>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSave} 
            isLoading={saving}
            isDisabled={loading}
          >
            저장
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default WorkerSelectionModal;
